import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { COUNTRIES, CURRENCIES, regionLabelFor } from "@/lib/countries";

const REGION_PLACEHOLDER = {
  US: "California", CA: "Ontario", AE: "Dubai",
  AU: "New South Wales", IN: "Maharashtra",
};

export default function LocationStep({ profile, saveField }) {
  const onCountryChange = (e) => {
    const code = e.target.value;
    const c = COUNTRIES.find((x) => x.code === code);
    const patch = { country_code: code, country: c ? c.name : profile.country };
    if (c && !profile.preferred_currency) patch.preferred_currency = c.currency;
    if (!profile.timezone) {
      try { patch.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* noop */ }
    }
    saveField(patch);
  };

  const detectTimezone = () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) { saveField({ timezone: tz }); toast.success(`Detected time zone: ${tz}`); }
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ob-country">Country</Label>
          <select
            id="ob-country"
            data-testid="onboarding-country"
            value={profile.country_code || ""}
            onChange={onCountryChange}
            className="w-full mt-1.5 bg-card border border-border px-4 py-2 rounded-md"
          >
            <option value="">— select country —</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <Label htmlFor="ob-region">{regionLabelFor(profile.country_code)}</Label>
          <Input
            id="ob-region"
            placeholder={REGION_PLACEHOLDER[profile.country_code] || "State / Province"}
            className="mt-1.5"
            value={profile.region || ""}
            onChange={(e) => saveField({ region: e.target.value })}
            data-testid="onboarding-region"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ob-city">City</Label>
          <Input
            id="ob-city" placeholder="San Francisco" className="mt-1.5"
            value={profile.city || ""}
            onChange={(e) => saveField({ city: e.target.value })}
            data-testid="onboarding-city"
          />
        </div>
        <div>
          <Label htmlFor="ob-currency">Preferred currency</Label>
          <select
            id="ob-currency"
            data-testid="onboarding-currency"
            value={profile.preferred_currency || ""}
            onChange={(e) => saveField({ preferred_currency: e.target.value || null })}
            className="w-full mt-1.5 bg-card border border-border px-4 py-2 rounded-md"
          >
            <option value="">— country default —</option>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <div className="flex items-baseline justify-between">
          <Label htmlFor="ob-tz">Time zone (IANA)</Label>
          <button
            type="button"
            data-testid="onboarding-detect-tz"
            onClick={detectTimezone}
            className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Detect
          </button>
        </div>
        <Input
          id="ob-tz" placeholder="America/New_York" className="mt-1.5"
          value={profile.timezone || ""}
          onChange={(e) => saveField({ timezone: e.target.value })}
          data-testid="onboarding-timezone"
        />
      </div>
      <div className="text-[11px] text-muted-foreground flex items-start gap-2">
        <MapPin size={12} className="shrink-0 mt-0.5" />
        <span>You can skip this — but with it, every AI answer uses your local currency, tax context, retirement vehicles, and employment norms.</span>
      </div>
    </div>
  );
}
